import dayjs, { Dayjs } from 'dayjs';
import { DatePreset } from 'app/components/datepicker-dropdown/enums';
import {
  getDatesFromPreset,
  getDatesWithConstraints,
} from 'app/components/datepicker-dropdown/utils';
import {
  AnalyticsDateRange,
  getAnalyticsMinSelectableDate,
} from 'app/modules/analytics/utils/analyticsDateLimit';

/**
 * The only date presets analytics offers. Every other range a user can pick is a
 * whole calendar month; nothing else is selectable.
 */
export const ANALYTICS_DATE_PRESETS = [
  DatePreset.THIS_MONTH,
  DatePreset.LAST_MONTH,
  DatePreset.LAST_30_DAYS,
] as const;

export type AnalyticsDatePreset = (typeof ANALYTICS_DATE_PRESETS)[number];

export type AnalyticsRangeShape =
  | { kind: 'preset'; preset: AnalyticsDatePreset }
  | { kind: 'month'; month: Dayjs }
  | { kind: 'unsupported' };

function isAnalyticsDatePreset(
  value: string | null | undefined,
): value is AnalyticsDatePreset {
  return ANALYTICS_DATE_PRESETS.includes(value as AnalyticsDatePreset);
}

function matchesPreset(
  preset: AnalyticsDatePreset,
  startDate: Dayjs,
  endDate: Dayjs,
): boolean {
  const presetDates = getDatesWithConstraints(getDatesFromPreset(preset), {
    maxEndDate: dayjs().endOf('day'),
  });

  return (
    presetDates.startDate != null &&
    presetDates.endDate != null &&
    presetDates.startDate.isSame(startDate, 'day') &&
    presetDates.endDate.isSame(endDate, 'day')
  );
}

/**
 * Whether a range covers exactly one calendar month end to end.
 *
 * Independent of {@link classifyAnalyticsRange}: last month is both a preset and a
 * whole month, and which of those matters depends on the question being asked.
 */
export function isWholeCalendarMonth(
  startDate: Dayjs | undefined,
  endDate: Dayjs | undefined,
): boolean {
  return (
    startDate != null &&
    endDate != null &&
    startDate.isValid() &&
    endDate.isValid() &&
    startDate.isSame(startDate.startOf('month'), 'day') &&
    endDate.isSame(startDate.endOf('month'), 'day')
  );
}

/**
 * Classifies a selected range as one of the shapes analytics supports.
 *
 * `storedPreset` is only a hint — a range is matched on its dates, so a stale or
 * absent preset never produces a shape the dates do not back up.
 */
export function classifyAnalyticsRange(
  range: Partial<AnalyticsDateRange>,
  storedPreset?: string | null,
): AnalyticsRangeShape {
  const { startDate, endDate } = range;

  if (
    startDate == null ||
    endDate == null ||
    !startDate.isValid() ||
    !endDate.isValid() ||
    endDate.isBefore(startDate, 'day')
  ) {
    return { kind: 'unsupported' };
  }

  if (
    isAnalyticsDatePreset(storedPreset) &&
    matchesPreset(storedPreset, startDate, endDate)
  ) {
    return { kind: 'preset', preset: storedPreset };
  }

  const matched = ANALYTICS_DATE_PRESETS.find((preset) =>
    matchesPreset(preset, startDate, endDate),
  );

  if (matched) {
    return { kind: 'preset', preset: matched };
  }

  if (isWholeCalendarMonth(startDate, endDate)) {
    return { kind: 'month', month: startDate.startOf('month') };
  }

  return { kind: 'unsupported' };
}

/**
 * The whole month containing a date can start before the oldest selectable day, so
 * recovering into it would reach below the floor the caller just enforced — and below
 * the picker's own `minDate`.
 */
function clampMonthStartToHistoryFloor(monthStart: Dayjs): Dayjs {
  const floor = getAnalyticsMinSelectableDate();

  return monthStart.isBefore(floor, 'day') ? floor : monthStart;
}

export interface AnalyticsRangeRecovery {
  startDate: Dayjs;
  endDate: Dayjs;
  preset: AnalyticsDatePreset | null;
}

function thisMonthRecovery(): AnalyticsRangeRecovery {
  const dates = getDatesWithConstraints(
    getDatesFromPreset(DatePreset.THIS_MONTH),
    { maxEndDate: dayjs().endOf('day') },
  );

  return {
    startDate: dates.startDate ?? dayjs().startOf('month'),
    endDate: dates.endDate ?? dayjs().endOf('day'),
    preset: DatePreset.THIS_MONTH,
  };
}

/**
 * Maps a range analytics can no longer select onto one it can: the whole calendar
 * month containing its start, or this month when that start is unusable.
 *
 * Supported ranges are returned untouched, so this is safe to run over any
 * persisted or shared range before it reaches the picker.
 */
export function recoverAnalyticsRange(
  range: Partial<AnalyticsDateRange>,
  storedPreset?: string | null,
): AnalyticsRangeRecovery {
  const shape = classifyAnalyticsRange(range, storedPreset);

  if (shape.kind === 'preset') {
    return {
      startDate: range.startDate as Dayjs,
      endDate: range.endDate as Dayjs,
      preset: shape.preset,
    };
  }

  if (shape.kind === 'month') {
    return {
      startDate: range.startDate as Dayjs,
      endDate: range.endDate as Dayjs,
      preset: null,
    };
  }

  const { startDate } = range;

  if (
    startDate == null ||
    !startDate.isValid() ||
    startDate.isAfter(dayjs(), 'day')
  ) {
    return thisMonthRecovery();
  }

  const monthStart = startDate.startOf('month');
  const monthDates = getDatesWithConstraints(
    {
      startDate: clampMonthStartToHistoryFloor(monthStart),
      endDate: monthStart.endOf('month'),
    },
    { maxEndDate: dayjs().endOf('day') },
  );

  if (monthDates.startDate == null || monthDates.endDate == null) {
    return thisMonthRecovery();
  }

  const recoveredShape = classifyAnalyticsRange({
    startDate: monthDates.startDate,
    endDate: monthDates.endDate,
  });

  return {
    startDate: monthDates.startDate,
    endDate: monthDates.endDate,
    preset: recoveredShape.kind === 'preset' ? recoveredShape.preset : null,
  };
}
